import { motion } from 'motion/react';
import { Phone, Mail, MessageCircle, Building2, HelpCircle } from 'lucide-react';
import { FaLinkedin, FaInstagram, FaFacebook, FaTiktok, FaWhatsapp, FaXTwitter } from '@/components/icons/SocialIcons';
import { Link } from 'react-router-dom';
import PageGlow from "@/components/PageGlow";
import HeroWave from "@/components/HeroWave";
import ContactForm from "@/components/ContactForm";
import { brand } from "@/config/brand";

const GRAD    = '#5048E5';
const fg      = 'hsl(var(--foreground))';
const fgMuted = 'hsl(var(--muted-foreground))';
const border  = 'hsl(var(--border))';

type ContactMethod =
  | { type: 'a'; icon: React.ElementType; label: string; value: string; sub: string; href: string; iconBg: string; iconColor: string; external: boolean; BrandIcon?: React.ElementType }
  | { type: 'link'; icon: React.ElementType; label: string; value: string; sub: string; to: string; iconBg: string; iconColor: string };

const CONTACT_METHODS: ContactMethod[] = [
  { type: 'a',    icon: Phone,         label: 'Phone',    value: brand.contact.phoneDisplay,               sub: `Call us directly — ${brand.contact.availability}`,  href: `tel:${brand.contact.phoneE164}`,                                                                                                  iconBg: 'hsla(243,75%,59%,0.14)', iconColor: GRAD,               external: false },
  { type: 'a',    icon: Mail,          label: 'Email',    value: brand.contact.email, sub: 'We reply within one working day',           href: `mailto:${brand.contact.email}`,                                                                                  iconBg: 'hsla(243,75%,59%,0.14)', iconColor: GRAD,               external: false },
  { type: 'a',    icon: MessageCircle, label: 'WhatsApp', value: 'Message us on WhatsApp',     sub: 'Quickest way to get a response',     href: `https://wa.me/${brand.contact.phoneE164.replace('+', '')}?text=Hi%20there,%20I'd%20like%20to%20enquire%20about%20your%20services`, iconBg: 'hsla(243,75%,59%,0.14)', iconColor: GRAD,               external: true, BrandIcon: FaWhatsapp },
  { type: 'link', icon: HelpCircle,    label: 'FAQ',      value: 'Browse our FAQ',             sub: 'Pricing, timelines & more answered', to: '/services#faq-section',                                                                                                iconBg: 'hsla(243,75%,59%,0.14)', iconColor: GRAD },
];

const SOCIAL_LINKS = [
  { icon: FaLinkedin,  label: 'LinkedIn',    handle: brand.name,  href: brand.social.linkedin,   iconBg: 'hsla(210,100%,45%,0.12)', iconColor: '#0a66c2' },
  { icon: FaInstagram, label: 'Instagram',   handle: brand.social.instagramHandle, href: brand.social.instagram,  iconBg: 'hsla(330,80%,55%,0.12)',  iconColor: '#e1306c' },
  { icon: FaXTwitter,  label: 'Twitter / X', handle: '@DigitalBaffled',    href: 'https://x.com/DigitalBaffled',                 iconBg: 'hsla(0,0%,10%,0.08)',     iconColor: 'hsl(var(--foreground))' },
  { icon: FaFacebook,  label: 'Facebook',    handle: brand.name,  href: brand.social.facebook,   iconBg: 'hsla(220,80%,50%,0.12)',  iconColor: '#1877f2' },
  { icon: FaTiktok,    label: 'TikTok',      handle: brand.social.tiktokHandle, href: brand.social.tiktok,    iconBg: 'hsla(0,0%,5%,0.10)',      iconColor: '#010101' },
];

const fadeUp = {
  hidden:  { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
};
const stagger = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const MethodRow = ({ icon: Icon, label, value, sub, iconBg, iconColor, BrandIcon }: { icon: React.ElementType; label: string; value: string; sub: string; iconBg: string; iconColor: string; BrandIcon?: React.ElementType }) => (
  <>
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: iconBg }}>
      {BrandIcon ? <BrandIcon size={18} color={iconColor} /> : <Icon size={16} style={{ color: iconColor }} />}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-bold leading-tight" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value}</p>
      <p className="mt-0.5 text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{sub}</p>
    </div>
    <span className="shrink-0 text-xs font-semibold" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{label} →</span>
  </>
);

// JSON-LD lives in ./contact.seo so route head() stays lightweight.

export default function ContactPage() {
  return (
    <>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-10 md:py-16" style={{ background: 'hsl(var(--background))' }}>
        <HeroWave fadeBottom={58} bleed="both" />
        <PageGlow />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.h1 variants={fadeUp} className="mb-4 text-4xl font-bold md:text-5xl"
              style={{ fontFamily: 'Outfit, sans-serif', color: fg, lineHeight: 1.08 }}>
              We'd love to{' '}
              <span style={{ background: `linear-gradient(135deg, ${GRAD} 0%, hsl(14 100% 57%) 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                hear from you
              </span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base leading-relaxed md:text-lg"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
              Whether you have a question or just want to say hello — reach us through any of the channels below.
            </motion.p>
            <motion.p variants={fadeUp} className="mx-auto mt-4 max-w-xl text-sm font-semibold leading-relaxed"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fg }}>
              We'll reply within one working day. When you enquire, a real person reads it and gets back to you with honest, plain-English answers — no pushy sales calls, no pressure.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Contact methods ── */}
      <section className="py-8" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-2xl px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="flex flex-col">
            {CONTACT_METHODS.map((method, i) =>
              method.type === 'link' ? (
                <motion.div key={method.label} variants={fadeUp} style={{ borderTop: i === 0 ? `1px solid ${border}` : undefined, borderBottom: `1px solid ${border}` }}>
                  <Link
                    to={method.to}
                    className="flex items-center gap-4 py-5 transition-opacity hover:opacity-70"
                    style={{ textDecoration: 'none' }}>
                    <MethodRow icon={method.icon} label={method.label} value={method.value} sub={method.sub} iconBg={method.iconBg} iconColor={method.iconColor} />
                  </Link>
                </motion.div>
              ) : (
                <motion.div key={method.label} variants={fadeUp} style={{ borderTop: i === 0 ? `1px solid ${border}` : undefined, borderBottom: `1px solid ${border}` }}>
                  <a
                    href={method.href}
                    {...(method.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="flex items-center gap-4 py-5 transition-opacity hover:opacity-70"
                    style={{ textDecoration: 'none' }}>
                    <MethodRow icon={method.icon} label={method.label} value={method.value} sub={method.sub} iconBg={method.iconBg} iconColor={method.iconColor} BrandIcon={method.BrandIcon} />
                  </a>
                </motion.div>
              )
            )}
          </motion.div>
        </div>
      </section>

      {/* ── Follow us ── */}
      <section className="py-12" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-2xl px-6">
          <motion.div>
            <p className="mb-6 text-xs font-bold uppercase tracking-widest" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Follow us</p>
            <div className="flex flex-wrap gap-5">
              {SOCIAL_LINKS.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2.5 transition-opacity hover:opacity-70"
                  style={{ textDecoration: 'none' }}>
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: s.iconBg }}>
                    {s.label === 'Instagram' ? (
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                        <defs>
                          <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#f09433" />
                            <stop offset="25%" stopColor="#e6683c" />
                            <stop offset="50%" stopColor="#dc2743" />
                            <stop offset="75%" stopColor="#cc2366" />
                            <stop offset="100%" stopColor="#bc1888" />
                          </linearGradient>
                        </defs>
                        <path fill="url(#ig-grad)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    ) : (
                      <s.icon size={17} color={s.iconColor} />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-bold leading-tight" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{s.label}</p>
                    <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{s.handle}</p>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Send us a message ── */}
      <section className="py-8" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-2xl px-6">
          <motion.div style={{ borderTop: `1px solid ${border}`, paddingTop: '2rem' }}>
            <p className="mb-1 text-xs font-bold uppercase tracking-widest" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Send us a message</p>
            <p className="mb-6 text-sm" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Prefer to write? Drop us a line and we'll reply within one working day — no pushy sales calls.</p>
            <ContactForm />
          </motion.div>
        </div>
      </section>

      {/* ── Company info ── */}
      <section className="px-6 pb-20" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-2xl">
          <motion.div style={{ borderTop: `1px solid ${border}`, paddingTop: '2rem' }}>
            <div className="flex items-start gap-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: 'hsla(243,75%,59%,0.12)' }}>
                <Building2 size={16} style={{ color: GRAD }} />
              </div>
              <div className="grid gap-4 text-sm sm:grid-cols-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider" style={{ color: fgMuted }}>Registered Name</p>
                  <p className="font-semibold" style={{ color: fg }}>{brand.legalName}</p>
                </div>
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider" style={{ color: fgMuted }}>Company Number</p>
                  <p className="font-semibold" style={{ color: fg }}>{brand.companyNumber}</p>
                </div>
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider" style={{ color: fgMuted }}>Registered Address</p>
                  <p className="font-semibold leading-relaxed" style={{ color: fg }}>{brand.contact.addressInline}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
